import { z } from 'zod';

import { PUT_API_MODULE_NAMES } from '@enroller/utils';
import { API_FIELDS_APPLICATION as APPLICATION, API_FIELDS_APPLICATION_PET as APPLICATION_PET } from '@enroller/admissions-components';

import { mapYesNoFieldsToBoolean, yesNoToBoolean, booleanStringToYesNo } from '../../../utils/fieldDatatransformation';
import { getDependentFieldOptionalSchema, getDependentFieldSchema } from '../../../constants/sharedSchemas';

import type { PageResponseData } from '../queries/graphqlResponse';
import type { StudentApplicationValues } from '../StudentApplication';

export enum HOUSING_PAGE_SECTIONS {
  housing = 'housingSection',
  pets = 'petsSection',
  placement = 'placement',
  food = 'food',
  allergies = 'allergies',
}

const petsSchema = z.array(
  z.object({
    [APPLICATION_PET.name]: z.string().min(1, 'Pet name is required'),
    [APPLICATION_PET.type]: z.string().min(1, 'Type of animal is required'),
    [APPLICATION_PET.size]: z.string().min(1, 'Relative size of animal is required'),
  }),
);

const petsOptionalSchema = z.array(
  z.object({
    [APPLICATION_PET.name]: z.string().optional(),
    [APPLICATION_PET.type]: z.string().optional(),
    [APPLICATION_PET.size]: z.string().optional(),
  }),
);

// Schema for when a student owns pets
const hasPetsSchema = z.object({
  [APPLICATION.doNotOwnPets]: z.literal(false),
  [APPLICATION.petsListGET]: petsSchema,
  [APPLICATION.adjustToIndoorPet]: getDependentFieldSchema('other', 'no'),
  [APPLICATION.afraidOfPets]: getDependentFieldSchema('other', 'yes'),
});

// Schema for when a student does not own pets
const noPetsSchema = z.object({
  [APPLICATION.doNotOwnPets]: z.literal(true),
  [APPLICATION.petsListGET]: petsOptionalSchema,
  [APPLICATION.adjustToIndoorPet]: getDependentFieldOptionalSchema('other'),
  [APPLICATION.afraidOfPets]: getDependentFieldOptionalSchema('other'),
});

// Discriminated union based on boolean value
export const petsSectionSchema = z.discriminatedUnion(APPLICATION.doNotOwnPets, [hasPetsSchema, noPetsSchema]);

export const housingSchema = {
  required: z.object({
    [HOUSING_PAGE_SECTIONS.housing]: z.object({
      [APPLICATION.youngChildrenFeelings]: z.string().min(1, 'How you feel about children is required'),
      [APPLICATION.attendReligiousServices]: z.string().min(1, 'This field is required'),
      [APPLICATION.willAttendWithHostFamily]: z.string().min(1, 'This field is required'),
      [APPLICATION.religiousPreference]: getDependentFieldSchema('other', 'Other'),
      [APPLICATION.understandNoSmoking]: z.boolean().refine((value) => value, {
        message: 'You must agree to this before continuing',
      }),
      [APPLICATION.adjustToSmokers]: z.string().min(1, 'This field is required'),
    }),

    [HOUSING_PAGE_SECTIONS.pets]: petsSectionSchema,

    [HOUSING_PAGE_SECTIONS.placement]: z.object({
      [APPLICATION.acceptDoublePlacement]: z.string().min(1, 'This field is required'),
      [APPLICATION.acceptSingleHost]: z.string().min(1, 'This field is required'),
      [APPLICATION.acceptSameSexCouple]: z.string().min(1, 'This field is required'),
      [APPLICATION.acceptSmallSchool]: z.string().min(1, 'This field is required'),
    }),

    [HOUSING_PAGE_SECTIONS.food]: z.object({
      [APPLICATION.dietRestricted]: z
        .object({
          value: z.string().min(1, 'This field is required'),
          [APPLICATION.prepareOwnMeals]: z.string().optional(),
        })
        .refine(
          (data) => {
            // Make ownMealPreparation field required if the value is "yes"
            if (data.value === 'yes') {
              return !!data[APPLICATION.prepareOwnMeals] && data[APPLICATION.prepareOwnMeals].trim() !== '';
            }
            return true;
          },
          {
            message: 'This field is required',
            path: [APPLICATION.prepareOwnMeals],
          },
        ),
      [APPLICATION.adjustToVegetarian]: z.string().min(1, 'This field is required'),
    }),

    [HOUSING_PAGE_SECTIONS.allergies]: z.object({
      [APPLICATION.allergicToAnimals]: z.string().min(1, 'This field is required'),
      allergySeverity: z.object({
        [APPLICATION.allergyDogReaction]: z.string().min(1, 'This field is required'),
        [APPLICATION.allergyCatReaction]: z.string().min(1, 'This field is required'),
        [APPLICATION.allergyHorseReaction]: z.string().min(1, 'This field is required'),
        [APPLICATION.allergyOtherAnimal]: z
          .object({
            value: z.string().min(1, 'This field is required'),
            [APPLICATION.allergyOtherAnimalExplanation]: z.string().optional(),
          })
          .transform((data) => {
            if (data.value === 'None') {
              // Automatically set the explanation to an empty string
              return {
                ...data,
                [APPLICATION.allergyOtherAnimalExplanation]: '',
              };
            }
            return data;
          })
          .refine(
            (data) => {
              // Make otherAnimalExplanation field required if the value is not "None"
              if (data.value !== 'None') {
                return !!data[APPLICATION.allergyOtherAnimalExplanation] && data[APPLICATION.allergyOtherAnimalExplanation].trim() !== '';
              }
              return true;
            },
            {
              message: 'This field is required',
              path: [APPLICATION.allergyOtherAnimalExplanation],
            },
          ),
      }),
      [APPLICATION.nonAllergicDogBreeds]: z.string().min(1, 'This field is required'),

      [APPLICATION.dogIndoorsNotInRoom]: z.string().min(1, 'This field is required'),
      [APPLICATION.dogOnlyAtNight]: z.string().min(1, 'This field is required'),
      [APPLICATION.dogOccasionallyIndoors]: z.string().min(1, 'This field is required'),
      [APPLICATION.dogOutsideOnly]: z.string().min(1, 'This field is required'),
      [APPLICATION.catIndoorsNotInRoom]: z.string().min(1, 'This field is required'),
      [APPLICATION.catOnlyAtNight]: z.string().min(1, 'This field is required'),
      [APPLICATION.catOccasionallyIndoors]: z.string().min(1, 'This field is required'),
      [APPLICATION.catOutsideOnly]: z.string().min(1, 'This field is required'),

      [APPLICATION.allergicToDust]: z.string().min(1, 'This field is required'),
      [APPLICATION.cannotLiveWithCarpet]: z.string().min(1, 'This field is required'),
      [APPLICATION.allergicToFood]: z.string().min(1, 'This field is required'),
      [APPLICATION.listFoodAllergies]: z.string().min(1, 'This field is required'),
      [APPLICATION.specialMeals]: z.string().min(1, 'This field is required'),
      [APPLICATION.makeSpecialMeals]: z.string().min(1, 'This field is required'),
      [APPLICATION.allergicToPollen]: z.string().min(1, 'This field is required'),
      [APPLICATION.wearsGlassesOrContacts]: z.string().min(1, 'This field is required'),
    }),
  }),
  optional: z.object({
    [HOUSING_PAGE_SECTIONS.housing]: z.object({
      [APPLICATION.youngChildrenFeelings]: z.string().optional(),
      [APPLICATION.attendReligiousServices]: z.string().optional(),
      [APPLICATION.willAttendWithHostFamily]: z.string().optional(),
      [APPLICATION.religiousPreference]: getDependentFieldOptionalSchema('other'),
      [APPLICATION.understandNoSmoking]: z.boolean(),
      [APPLICATION.adjustToSmokers]: z.string().optional(),
    }),

    [HOUSING_PAGE_SECTIONS.pets]: z.object({
      [APPLICATION.doNotOwnPets]: z.boolean(),
      [APPLICATION.petsListGET]: z.array(
        z.object({
          [APPLICATION_PET.name]: z.string().optional(),
          [APPLICATION_PET.type]: z.string().optional(),
          [APPLICATION_PET.size]: z.string().optional(),
        }),
      ),
      [APPLICATION.adjustToIndoorPet]: getDependentFieldOptionalSchema('other'),
      [APPLICATION.afraidOfPets]: getDependentFieldOptionalSchema('other'),
    }),

    [HOUSING_PAGE_SECTIONS.placement]: z.object({
      [APPLICATION.acceptDoublePlacement]: z.string().optional(),
      [APPLICATION.acceptSingleHost]: z.string().optional(),
      [APPLICATION.acceptSameSexCouple]: z.string().optional(),
      [APPLICATION.acceptSmallSchool]: z.string().optional(),
    }),

    [HOUSING_PAGE_SECTIONS.food]: z.object({
      [APPLICATION.dietRestricted]: z.object({
        value: z.string().optional(),
        [APPLICATION.prepareOwnMeals]: z.string().optional(),
      }),
      [APPLICATION.adjustToVegetarian]: z.string().optional(),
    }),

    [HOUSING_PAGE_SECTIONS.allergies]: z.object({
      [APPLICATION.allergicToAnimals]: z.string().optional(),
      allergySeverity: z.object({
        [APPLICATION.allergyDogReaction]: z.string().optional(),
        [APPLICATION.allergyCatReaction]: z.string().optional(),
        [APPLICATION.allergyHorseReaction]: z.string().optional(),
        [APPLICATION.allergyOtherAnimal]: z.object({
          value: z.string().optional(),
          [APPLICATION.allergyOtherAnimalExplanation]: z.string().optional(),
        }),
      }),
      [APPLICATION.nonAllergicDogBreeds]: z.string().optional(),
      [APPLICATION.dogIndoorsNotInRoom]: z.string().optional(),
      [APPLICATION.dogOnlyAtNight]: z.string().optional(),
      [APPLICATION.dogOccasionallyIndoors]: z.string().optional(),
      [APPLICATION.dogOutsideOnly]: z.string().optional(),
      [APPLICATION.catIndoorsNotInRoom]: z.string().optional(),
      [APPLICATION.catOnlyAtNight]: z.string().optional(),
      [APPLICATION.catOccasionallyIndoors]: z.string().optional(),
      [APPLICATION.catOutsideOnly]: z.string().optional(),
      [APPLICATION.allergicToDust]: z.string().optional(),
      [APPLICATION.cannotLiveWithCarpet]: z.string().optional(),
      [APPLICATION.allergicToFood]: z.string().optional(),
      [APPLICATION.listFoodAllergies]: z.string().optional(),
      [APPLICATION.specialMeals]: z.string().optional(),
      [APPLICATION.makeSpecialMeals]: z.string().optional(),
      [APPLICATION.allergicToPollen]: z.string().optional(),
      [APPLICATION.wearsGlassesOrContacts]: z.string().optional(),
    }),
  }),
};

export const defaultValues: HousingFormValues = {
  [HOUSING_PAGE_SECTIONS.housing]: {
    [APPLICATION.youngChildrenFeelings]: '',
    [APPLICATION.attendReligiousServices]: '',
    [APPLICATION.willAttendWithHostFamily]: '',
    [APPLICATION.religiousPreference]: {
      value: '',
      other: '',
    },
    [APPLICATION.understandNoSmoking]: false,
    [APPLICATION.adjustToSmokers]: '',
  },

  [HOUSING_PAGE_SECTIONS.pets]: {
    [APPLICATION.doNotOwnPets]: false,
    [APPLICATION.petsListGET]: [
      {
        [APPLICATION_PET.name]: '',
        [APPLICATION_PET.type]: '',
        [APPLICATION_PET.size]: '',
      },
    ],
    [APPLICATION.adjustToIndoorPet]: {
      value: '',
      other: '',
    },
    [APPLICATION.afraidOfPets]: {
      value: '',
      other: '',
    },
  },

  [HOUSING_PAGE_SECTIONS.placement]: {
    [APPLICATION.acceptDoublePlacement]: '',
    [APPLICATION.acceptSingleHost]: '',
    [APPLICATION.acceptSameSexCouple]: '',
    [APPLICATION.acceptSmallSchool]: '',
  },

  [HOUSING_PAGE_SECTIONS.food]: {
    [APPLICATION.dietRestricted]: { value: '', [APPLICATION.prepareOwnMeals]: '' },
    [APPLICATION.adjustToVegetarian]: '',
  },

  [HOUSING_PAGE_SECTIONS.allergies]: {
    [APPLICATION.allergicToAnimals]: '',
    allergySeverity: {
      [APPLICATION.allergyDogReaction]: '',
      [APPLICATION.allergyCatReaction]: '',
      [APPLICATION.allergyHorseReaction]: '',
      [APPLICATION.allergyOtherAnimal]: {
        value: '',
        [APPLICATION.allergyOtherAnimalExplanation]: '',
      },
    },
    [APPLICATION.nonAllergicDogBreeds]: '',
    [APPLICATION.dogIndoorsNotInRoom]: '',
    [APPLICATION.dogOnlyAtNight]: '',
    [APPLICATION.dogOccasionallyIndoors]: '',
    [APPLICATION.dogOutsideOnly]: '',
    [APPLICATION.catIndoorsNotInRoom]: '',
    [APPLICATION.catOnlyAtNight]: '',
    [APPLICATION.catOccasionallyIndoors]: '',
    [APPLICATION.catOutsideOnly]: '',
    [APPLICATION.allergicToDust]: '',
    [APPLICATION.cannotLiveWithCarpet]: '',
    [APPLICATION.allergicToFood]: '',
    [APPLICATION.listFoodAllergies]: '',
    [APPLICATION.specialMeals]: '',
    [APPLICATION.makeSpecialMeals]: '',
    [APPLICATION.allergicToPollen]: '',
    [APPLICATION.wearsGlassesOrContacts]: '',
  },
};

export type HousingFormValues = z.infer<(typeof housingSchema)['required']> | z.infer<(typeof housingSchema)['optional']>;

export const createHousingFormResponse = (studentApplication: PageResponseData['housing']): HousingFormValues => {
  const petsList = studentApplication?.[APPLICATION.petsListGET]?._data ?? [];

  return {
    [HOUSING_PAGE_SECTIONS.housing]: {
      [APPLICATION.youngChildrenFeelings]: studentApplication?.[APPLICATION.youngChildrenFeelings]?.value ?? '',
      [APPLICATION.attendReligiousServices]: studentApplication?.[APPLICATION.attendReligiousServices]?.value ?? '',
      [APPLICATION.willAttendWithHostFamily]: studentApplication?.[APPLICATION.willAttendWithHostFamily]?.value ?? '',
      [APPLICATION.religiousPreference]: {
        value: studentApplication?.[APPLICATION.religiousPreference]?.value ?? '',
        other: studentApplication?.[APPLICATION.religiousPreferenceOther]?.value ?? '',
      },
      [APPLICATION.understandNoSmoking]: !!studentApplication?.[APPLICATION.understandNoSmoking]?.value,
      [APPLICATION.adjustToSmokers]: studentApplication?.[APPLICATION.adjustToSmokers]?.value ?? '',
    },

    [HOUSING_PAGE_SECTIONS.pets]: {
      [APPLICATION.doNotOwnPets]: Boolean(studentApplication?.[APPLICATION.doNotOwnPets]?.value),
      [APPLICATION.petsListGET]: petsList.map((pet) => ({
        [APPLICATION_PET.id]: pet?.[APPLICATION_PET.id]?.value ?? '',
        [APPLICATION_PET.name]: pet?.[APPLICATION_PET.name]?.value ?? '',
        [APPLICATION_PET.size]: pet?.[APPLICATION_PET.size]?.value ?? '',
        [APPLICATION_PET.type]: pet?.[APPLICATION_PET.type]?.value ?? '',
      })),

      [APPLICATION.adjustToIndoorPet]: {
        value: booleanStringToYesNo(studentApplication?.[APPLICATION.adjustToIndoorPet]?.value ?? ''),
        other: studentApplication?.[APPLICATION.noIndoorPetExplanation]?.value ?? '',
      },
      [APPLICATION.afraidOfPets]: {
        value: booleanStringToYesNo(studentApplication?.[APPLICATION.afraidOfPets]?.value ?? ''),
        other: studentApplication?.[APPLICATION.afraidOfPetExplanation]?.value ?? '',
      },
    },

    [HOUSING_PAGE_SECTIONS.placement]: {
      [APPLICATION.acceptDoublePlacement]: booleanStringToYesNo(studentApplication?.[APPLICATION.acceptDoublePlacement]?.value ?? ''),
      [APPLICATION.acceptSingleHost]: studentApplication?.[APPLICATION.acceptSingleHost]?.value ?? '',
      [APPLICATION.acceptSameSexCouple]: studentApplication?.[APPLICATION.acceptSameSexCouple]?.value ?? '',
      [APPLICATION.acceptSmallSchool]: booleanStringToYesNo(studentApplication?.[APPLICATION.acceptSmallSchool]?.value ?? ''),
    },

    [HOUSING_PAGE_SECTIONS.food]: {
      [APPLICATION.dietRestricted]: {
        value: booleanStringToYesNo(studentApplication?.[APPLICATION.dietRestricted]?.value ?? ''),
        [APPLICATION.prepareOwnMeals]: booleanStringToYesNo(studentApplication?.[APPLICATION.prepareOwnMeals]?.value ?? ''),
      },
      [APPLICATION.adjustToVegetarian]: booleanStringToYesNo(studentApplication?.[APPLICATION.adjustToVegetarian]?.value ?? ''),
    },

    [HOUSING_PAGE_SECTIONS.allergies]: {
      [APPLICATION.allergicToAnimals]: booleanStringToYesNo(studentApplication?.[APPLICATION.allergicToAnimals]?.value ?? ''),
      allergySeverity: {
        [APPLICATION.allergyDogReaction]: studentApplication?.[APPLICATION.allergyDogReaction]?.value ?? '',
        [APPLICATION.allergyCatReaction]: studentApplication?.[APPLICATION.allergyCatReaction]?.value ?? '',
        [APPLICATION.allergyHorseReaction]: studentApplication?.[APPLICATION.allergyHorseReaction]?.value ?? '',
        [APPLICATION.allergyOtherAnimal]: {
          value: studentApplication?.[APPLICATION.allergyOtherAnimal]?.value ?? '',
          [APPLICATION.allergyOtherAnimalExplanation]: studentApplication?.[APPLICATION.allergyOtherAnimalExplanation]?.value ?? '',
        },
      },
      [APPLICATION.nonAllergicDogBreeds]: studentApplication?.[APPLICATION.nonAllergicDogBreeds]?.value ?? '',
      [APPLICATION.dogIndoorsNotInRoom]: booleanStringToYesNo(studentApplication?.[APPLICATION.dogIndoorsNotInRoom]?.value ?? ''),
      [APPLICATION.dogOnlyAtNight]: booleanStringToYesNo(studentApplication?.[APPLICATION.dogOnlyAtNight]?.value ?? ''),
      [APPLICATION.dogOccasionallyIndoors]: booleanStringToYesNo(studentApplication?.[APPLICATION.dogOccasionallyIndoors]?.value ?? ''),
      [APPLICATION.dogOutsideOnly]: booleanStringToYesNo(studentApplication?.[APPLICATION.dogOutsideOnly]?.value ?? ''),
      [APPLICATION.catIndoorsNotInRoom]: booleanStringToYesNo(studentApplication?.[APPLICATION.catIndoorsNotInRoom]?.value ?? ''),
      [APPLICATION.catOnlyAtNight]: booleanStringToYesNo(studentApplication?.[APPLICATION.catOnlyAtNight]?.value ?? ''),
      [APPLICATION.catOccasionallyIndoors]: booleanStringToYesNo(studentApplication?.[APPLICATION.catOccasionallyIndoors]?.value ?? ''),
      [APPLICATION.catOutsideOnly]: booleanStringToYesNo(studentApplication?.[APPLICATION.catOutsideOnly]?.value ?? ''),
      [APPLICATION.allergicToDust]: booleanStringToYesNo(studentApplication?.[APPLICATION.allergicToDust]?.value ?? ''),
      [APPLICATION.cannotLiveWithCarpet]: booleanStringToYesNo(studentApplication?.[APPLICATION.cannotLiveWithCarpet]?.value ?? ''),
      [APPLICATION.allergicToFood]: booleanStringToYesNo(studentApplication?.[APPLICATION.allergicToFood]?.value ?? ''),
      [APPLICATION.listFoodAllergies]: studentApplication?.[APPLICATION.listFoodAllergies]?.value ?? '',
      [APPLICATION.specialMeals]: booleanStringToYesNo(studentApplication?.[APPLICATION.specialMeals]?.value ?? ''),
      [APPLICATION.makeSpecialMeals]: booleanStringToYesNo(studentApplication?.[APPLICATION.makeSpecialMeals]?.value ?? ''),
      [APPLICATION.allergicToPollen]: booleanStringToYesNo(studentApplication?.[APPLICATION.allergicToPollen]?.value ?? ''),
      [APPLICATION.wearsGlassesOrContacts]: studentApplication?.[APPLICATION.wearsGlassesOrContacts]?.value ?? '',
    },
  };
};

export const createHousingPayload = (values: HousingFormValues, modulesID: StudentApplicationValues['modulesID']) => {
  const { [APPLICATION.religiousPreference]: religiousPreference, ...housingFields } = values[HOUSING_PAGE_SECTIONS.housing];
  const petsFields = values[HOUSING_PAGE_SECTIONS.pets];
  // TODO: implement pets list API save data

  const {
    [APPLICATION.acceptDoublePlacement]: acceptDoublePlacement = '',
    [APPLICATION.acceptSmallSchool]: acceptSmallSchool = '',
    ...placement
  } = values[HOUSING_PAGE_SECTIONS.placement];

  const { [APPLICATION.adjustToVegetarian]: adjustToVegetarian = '', [APPLICATION.dietRestricted]: diet } = values[HOUSING_PAGE_SECTIONS.food];
  const { allergySeverity, ...allergyFields } = values[HOUSING_PAGE_SECTIONS.allergies];
  const { [APPLICATION.allergyOtherAnimal]: allergyOtherAnimal, ...allergySeverityFields } = allergySeverity;

  return {
    data: {
      [PUT_API_MODULE_NAMES.STUDENTS_APPLICATIONS]: [
        {
          [APPLICATION.ID]: modulesID[PUT_API_MODULE_NAMES.STUDENTS_APPLICATIONS],
          [APPLICATION.religiousPreference]: religiousPreference.value ?? '',
          [APPLICATION.religiousPreferenceOther]: religiousPreference.other ?? '',
          ...housingFields,
          [APPLICATION.doNotOwnPets]: petsFields[APPLICATION.doNotOwnPets],
          [APPLICATION.adjustToIndoorPet]: yesNoToBoolean(petsFields[APPLICATION.adjustToIndoorPet].value as string),
          [APPLICATION.noIndoorPetExplanation]: petsFields[APPLICATION.adjustToIndoorPet].other,
          [APPLICATION.afraidOfPets]: yesNoToBoolean(petsFields[APPLICATION.afraidOfPets].value as string),
          [APPLICATION.afraidOfPetExplanation]: petsFields[APPLICATION.afraidOfPets].other,
          [APPLICATION.acceptDoublePlacement]: yesNoToBoolean(acceptDoublePlacement),
          [APPLICATION.acceptSmallSchool]: yesNoToBoolean(acceptSmallSchool),
          ...placement,
          [APPLICATION.adjustToVegetarian]: yesNoToBoolean(adjustToVegetarian),
          [APPLICATION.dietRestricted]: yesNoToBoolean(diet?.value || ''),
          [APPLICATION.prepareOwnMeals]: yesNoToBoolean(diet?.[APPLICATION.prepareOwnMeals] || ''),
          [APPLICATION.allergyOtherAnimal]: allergyOtherAnimal.value || '',
          [APPLICATION.allergyOtherAnimalExplanation]: allergyOtherAnimal[APPLICATION.allergyOtherAnimalExplanation] || '',
          ...allergySeverityFields,
          ...mapYesNoFieldsToBoolean(allergyFields),
        },
      ],
    },
  };
};
